import { array2String, extractExtraResource, extractNumber, fmtStr, string2Array } from '@/utils';
import { calculateCPU, calculateMemory } from '@/utils/calc-tools';
import get from 'lodash/get';
import { ContainerItem } from '../../container';
import { ContainerTypeEnum, DockerItem } from '../../docker-setting';
import { ContainerData, ContainerPort } from '../../types';
import { envToYaml, yamlToEnv } from './yaml-env';
import { lifecycleToYaml } from './yaml-lifecycle';
import { probeToYaml, yamlToProbe } from './yaml-probe';

// 转换容器数据到 yaml 数据
export function containerToYaml(item: ContainerItem): ContainerData {
  /** 这里过滤了 servicePort */
  const ports = item.portSetting?.map((port) => {
    const filteredPort: ContainerPort = {
      containerPort: port.containerPort,
      protocol: port.protocol,
      name: port.name,
    };
    return filteredPort;
  });
  const container: ContainerData = {
    name: item.containerSetting.containerName,
    image: item.containerSetting.image,
    imagePullPolicy: item.imagePullPolicy,
    ports,
    resources: {
      requests: {
        cpu: item.containerSetting.cpuReserve,
        memory: fmtStr('$1Mi', item.containerSetting.memoryReserve),
        [item.containerSetting.gpuType]: item.containerSetting.gpuLimit,
      },
      limits: {
        cpu: item.containerSetting.cpuLimit,
        memory: fmtStr('$1Mi', item.containerSetting.memoryLimit),
        [item.containerSetting.gpuType]: item.containerSetting.gpuLimit,
      },
    },
    livenessProbe: probeToYaml(item.livenessProbe),
    readinessProbe: probeToYaml(item.readinessProbe),
    startupProbe: probeToYaml(item.startupProbe),
    lifecycle: {
      postStart: lifecycleToYaml(item.lifecycle?.postStart),
      preStop: lifecycleToYaml(item.lifecycle?.preStop),
    },
    command: string2Array(item.command),
    args: string2Array(item.args),
    tty: item.tty,
    env: envToYaml(item.env),
    securityContext: item.securityContext,
    volumeMounts: item.volumeMounts,
  };
  console.log('container: ', container);
  return container;
}

export function yamlToContainer(
  item: ContainerData,
  containerType: ContainerTypeEnum,
): ContainerItem {
  if (!item) return undefined;

  const keyValues = extractExtraResource(item?.resources?.requests, ['cpu', 'memory']);

  let gpuType;
  let gpuLimit;
  if (keyValues.length > 0) {
    gpuType = keyValues[0].key;
    gpuLimit = keyValues[0].value;
  }

  const requestsMemory = get(item, 'resources.requests.memory');
  const limitsMemory = get(item, 'resources.limits.memory');
  const requestsCPU = get(item, 'resources.requests.cpu');
  const limitsCPU = get(item, 'resources.limits.cpu');
  /** 计算对应的内存和 cpu 资源, 返回数值 */
  const memoryReserveStr = calculateMemory([requestsMemory], { forceUnit: 'Mi' });
  const memoryLimitStr = calculateMemory([limitsMemory], { forceUnit: 'Mi' });
  const cpuReserveStr = calculateCPU([requestsCPU], { forceUnit: 'number' });
  const cpuLimitStr = calculateCPU([limitsCPU], { forceUnit: 'number' });

  const containerSetting: DockerItem = {
    containerName: item.name,
    image: item.image,
    imageOrigin: 'docker_hub',
    containerType,
    cpuReserve: extractNumber(cpuReserveStr) || undefined,
    cpuLimit: extractNumber(cpuLimitStr) || undefined,
    memoryReserve: extractNumber(memoryReserveStr) || undefined,
    memoryLimit: extractNumber(memoryLimitStr) || undefined,
    gpuType,
    gpuLimit,
  };

  const container: ContainerItem = {
    containerSetting,
    portSetting: item.ports,
    imagePullPolicy: item.imagePullPolicy,
    livenessProbe: item.livenessProbe,
    readinessProbe: item.readinessProbe,
    startupProbe: item.startupProbe,
    lifecycle: {
      postStart: yamlToProbe(item.lifecycle?.postStart),
      preStop: yamlToProbe(item.lifecycle?.preStop),
    },
    command: array2String(item.command),
    args: array2String(item.args),
    env: yamlToEnv(item.env),
    tty: item.tty,
    securityContext: item.securityContext,
    volumeMounts: item.volumeMounts,
  };

  return container;
}
